import { Controller } from "@hotwired/stimulus";

// Page-level controller for the booking confirmation page: the "Kontakta
// oss" support modal, and loading the late-baggage/seating pickers into
// their own modals (id from each button's data-modal-id, same hand-
// authored fixed-inset-0-backdrop dialog step2 uses for its own seat map --
// see step2/_booking_form.html.erb's #seating-dialog). Those pickers are
// served by .js.erb responses (format.js, matching BookingController's
// existing action contract) -- rewritten to dispatch a
// "booking:ancillary-loaded" event with the rendered HTML instead of doing
// jQuery injection directly, same approach as step3's verify_orderlines.js.erb.
export default class extends Controller {
  static targets = ["modal"];

  connect() {
    this.boundAncillaryLoaded = (event) => this.onAncillaryLoaded(event);
    document.addEventListener("booking:ancillary-loaded", this.boundAncillaryLoaded);
  }

  disconnect() {
    document.removeEventListener("booking:ancillary-loaded", this.boundAncillaryLoaded);
  }

  open() {
    this.modalTarget.classList.remove("hidden");
  }

  close() {
    this.modalTarget.classList.add("hidden");
  }

  // Wired on the modal backdrop itself -- event.currentTarget is that
  // backdrop, event.target is whatever was actually clicked, equal only
  // when the click didn't bubble up from the card content inside it.
  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.close();
  }

  // Baggage/seating fetch once and cache: the content div is marked
  // data-loaded="true" the first time real content lands in it (see
  // onAncillaryLoaded), so every click after that just reopens the same
  // modal -- picking seats or checking baggage boxes earlier in the visit
  // is still sitting there, not lost or re-fetched.
  loadAncillary(event) {
    const button = event.currentTarget;
    const modalEl = document.getElementById(button.dataset.modalId);
    const targetEl = document.getElementById(button.dataset.targetId);

    if (targetEl && targetEl.dataset.loaded === "true") {
      if (modalEl) modalEl.classList.remove("hidden");
      return;
    }

    button.disabled = true;
    // Opens right away showing the target's own default content (a
    // spinner -- see booking__ancillaries.html.erb) while the AJAX
    // response is still in flight; onAncillaryLoaded overwrites it below.
    if (modalEl) modalEl.classList.remove("hidden");

    $.ajax({
      url: button.dataset.url,
      type: "GET",
      success: () => {},
      error: () => {
        button.disabled = false;
        if (targetEl) targetEl.innerHTML = '<div class="py-8 text-center text-sm text-gray-500">Något gick fel, försök igen.</div>';
      },
    });
  }

  onAncillaryLoaded(event) {
    const { targetId, html } = event.detail;
    const targetEl = document.getElementById(targetId);
    if (!targetEl) return;
    targetEl.innerHTML = html;
    targetEl.dataset.loaded = "true";

    const button = document.querySelector(`[data-target-id="${targetId}"]`);
    if (button) button.disabled = false;

    // The seating picker's own JS (step2_seating.js, shared with step2's
    // seat map) needs an explicit call here -- a <script src> tag set via
    // .innerHTML (as above) is parsed but never executed, unlike a real
    // <script> tag on the page or jQuery's $.getScript. That script is
    // loaded for real, once, elsewhere on this page; this just re-runs
    // its setup against the seat-map HTML just injected above.
    if (targetId === "seating-content" && window.initSeatingScript) {
      window.initSeatingScript();
    }
  }

  closeAncillaryModal(event) {
    event.currentTarget.closest(".fixed.inset-0")?.classList.add("hidden");
  }

  closeAncillaryModalOnBackdrop(event) {
    if (event.target === event.currentTarget) event.currentTarget.classList.add("hidden");
  }
}
